"use client"

import { Button } from "@/components/ui/button"
import { MobileMenu } from "@/components/mobile-menu"
import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { usePathname } from "@/i18n/navigation"
import { ArrowRight } from "lucide-react"

interface SiteHeaderProps {
  navigationLinks: Array<{ name: string; href: string }>
}

export function SiteHeader({ navigationLinks }: SiteHeaderProps) {
  const t = useTranslations("nav")
  const pathname = usePathname()

  const isActiveLink = (href: string) => {
    if (href.startsWith("#")) return false
    if (href === "/") return pathname === "/"
    return pathname.startsWith(href)
  }

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60"
      role="banner"
    >
      <div className="max-w-6xl flex h-16 items-center justify-between px-4 md:px-6 mx-auto">
        <div className="flex items-center space-x-8">
          <Link href="/" className="flex items-center space-x-2" aria-label="ClubPilot Home">
            <Image
              src="/clubpilot-logo.svg"
              alt="ClubPilot"
              width={140}
              height={40}
              className="h-8 w-auto"
              priority
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-6" aria-label="Main navigation">
          {navigationLinks.map((link) => {
            const isActive = isActiveLink(link.href)
            const isAnchorLink = link.href.startsWith("#")
            const className = `text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 rounded-sm ${
              isActive
                ? "text-accent font-semibold"
                : "text-muted-foreground hover:text-foreground"
            }`
            
            if (isAnchorLink) {
              return (
                <a
                  key={link.name}
                  href={link.href}
                  className={className}
                >
                  {link.name}
                </a>
              )
            }
            
            return (
              <Link
                key={link.name}
                href={link.href as "/" | "/about" | "/impressum" | "/privacy" | "/terms" | "/gtc" | "/beta-terms" | "/waitlist"}
                className={className}
              >
                {link.name}
              </Link>
            )
          })}
          </nav>
        </div>

        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" className="hidden sm:inline-flex" asChild>
            <a href="https://app.clubpilot.at" target="_blank" rel="noopener noreferrer">
              {t("signIn")}
            </a>
          </Button>
          <Button size="sm" asChild>
            <Link href="/waitlist">
              {t("startFreeTrial")}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          <MobileMenu navigationLinks={navigationLinks} />
        </div>
      </div>
    </header>
  )
}

